import { Component, OnInit, NgZone } from '@angular/core';
import { LeftUiComponent } from '../left-ui/left-ui/left-ui.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AccountService } from '../../Utils/_services/account/account.service';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';
import { ICustomError } from '../../Utils/_models/network-response';
import { ILogin_Request, ILogin_Response } from '../../Utils/_services/account/account.model';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, LeftUiComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit {

  public loginModel: ILogin_Request = {
    usernameOrEmail: '',
    password: ''
  };
  public isLoading = false;
  public showPassword = false;
  public errorMessage = '';
  public rememberMe = false;



  constructor(
    private accountService: AccountService,
    private router: Router,
    private toastr: ToastrService,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.accountService.currentUser$.pipe(take(1)).subscribe((currentUser: any | null) => {
      if (currentUser) {
        const responseData = { data: currentUser };
        const redirectUrl = this.getRedirectUrl(responseData as any);
        this.ngZone.run(() => {
          this.router.navigate([redirectUrl], {
            state: {
              email: currentUser.email,
              accountId: currentUser.accountId || ''
            }
          });
        });
        return;
      }
    });

    const savedCredentials = localStorage.getItem('rememberedCredentials');
    if (savedCredentials) {
      const credentials = JSON.parse(savedCredentials);
      this.loginModel.usernameOrEmail = credentials.usernameOrEmail;
      this.rememberMe = true;
    }
  }

  onSubmit(): void {
    if (this.isLoading) return;

    if (!this.loginModel.usernameOrEmail || !this.loginModel.password) {
      this.errorMessage = 'Please enter both username/email and password.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.accountService.login(this.loginModel).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response.data && response.data.requires2FA) {
          this.toastr.success(response.msg || 'Verification Code Sent');
          this.ngZone.run(() => {
            this.router.navigate(['/auth/email-verification'], {
              state: {
                accountId: response.data!.accountId,
                email: response.data!.email,
                expirationTime: response.data!.expirationTime,
                isLoginVerification: true
              }
            });
          });
          return;
        }

        if (response.data && response.auth?.token) {
          if (this.rememberMe) {
            localStorage.setItem('rememberedCredentials', JSON.stringify({
              usernameOrEmail: this.loginModel.usernameOrEmail
            }));
          } else {
            localStorage.removeItem('rememberedCredentials');
          }

          this.toastr.success(response.msg || 'Login successful!');

          const redirectUrl = this.getRedirectUrl(response);
          this.ngZone.run(() => {
            this.router.navigate([redirectUrl], {
              state: {
                email: response.data?.email || this.loginModel.usernameOrEmail,
                accountId: response.data?.accountId || ''
              }
            });
          });
        } else {
          this.errorMessage = response.msg || 'Login failed. Please try again.';
          this.toastr.error(this.errorMessage);
        }
      },
      error: (err: ICustomError) => {
        this.isLoading = false;
        this.errorMessage = err.error?.msg || 'Login failed. Please check your credentials and try again.';
        this.toastr.error(this.errorMessage);
        console.error('Login error:', err);
      }
    });
  }



  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  navigateToRegister(): void {
    this.ngZone.run(() => {
      this.router.navigate(['/auth/register']);
    });
  }

  navigateToForgotPassword(): void {
    this.ngZone.run(() => {
      this.router.navigate(["/auth/forgot"]);
    });
  }

  clearError(): void {
    this.errorMessage = '';
  }

  private getRedirectUrl(response: any): string {
    const role = response.data?.role;
    
    // If registration is explicitly false or undefined (not completed) and not admin
    const isAdmin = role && (role.toLowerCase() === 'admin' || role.toLowerCase() === 'administrator' || role.toLowerCase() === 'super-admin');
    if (!response.data?.registrationCompleted && !isAdmin) {
      const step = response.data?.registrationStep || 1;
      
      if (step === 1) {
        return '/auth/email-verification';
      } else if (step === 2) {
        return '/auth/phone-verification';
      } else if (step === 3) {
        return '/auth/user-details';
      }
    }

    if (role && (role.toLowerCase() === 'admin' || role.toLowerCase() === 'administrator')) {
      return '/admin/dashboard';
    } else if (role && role.toLowerCase() === 'user') {
      return '/user/dashboard';
    }
    return '/';
  }
}
